<template>
  <div class="full-container">
      <!-- 搜索和筛选区域 -->
      <div class="search-bar" style="padding: 15px; border-bottom: 1px solid var(--border);">
        <div class="flex-wrap">
          <Input 
            v-model="searchText" 
            placeholder="搜索知识条目名称或内容" 
            clearable 
            style="width: 300px; margin-right: 10px;" 
            @on-enter="handleSearch"
          />
          <Select 
            v-model="filterType" 
            clearable 
            placeholder="筛选类型" 
            style="width: 150px; margin-right: 10px;"
          >
            <Option v-for="type in typeList" :key="type" :value="type">{{ type }}</Option>
          </Select>
          <Select 
            v-model="filterSyncState" 
            clearable 
            placeholder="筛选同步状态" 
            style="width: 150px; margin-right: 10px;"
          >
            <Option :value="0">未同步</Option>
            <Option :value="1">已更新</Option>
            <Option :value="2">切片中</Option>
            <Option :value="3">已同步</Option>
          </Select>
          <Button type="primary" icon="md-search" @click="handleSearch" style="margin-right: 10px;">搜索</Button>
          <Button type="success" icon="md-add" @click="handleAdd" style="margin-right: 10px;">新增</Button>
          <Button type="info" icon="md-download" @click="handleImport">从项目平台导入</Button>
        </div>
      </div>

      <!-- 表格区域 -->
      <div style="height: calc(100% - 80px); padding: 15px;position:relative">
        <BaseTable
          :columns="columns"
          :data="filteredData"
          :loading="loading"
          border
          @event="handleTableEvent"
        />
      </div>

      <!-- 编辑对话框 -->
      <Modal
        v-model="modalVisible"
        :title="modalTitle"
        width="900"
        @on-ok="handleSubmit"
        :loading="confirmLoading"
        @on-cancel="handleCancel"
      >
        <Form ref="formRef" :model="formData" :rules="rules" :label-width="120">
          <FormItem label="知识条目总结" prop="name">
            <Input v-model="formData.name" placeholder="请输入知识条目总结" />
          </FormItem>
          <FormItem label="条目类型" prop="type">
            <Input v-model="formData.type" placeholder="请输入条目类型" />
          </FormItem>
          <FormItem label="知识库内容" prop="content">
            <Input 
              type="textarea" 
              v-model="formData.content" 
              :rows="8" 
              placeholder="请输入知识库内容" 
            />
          </FormItem>
          <FormItem label="附件">
            <Input 
              type="textarea" 
              v-model="formData.files" 
              :rows="3" 
              placeholder="请输入附件信息（多个附件用换行分隔）" 
            />
          </FormItem>
          <FormItem label="作者">
            <Input v-model="formData.author" placeholder="请输入作者" />
          </FormItem>
          <FormItem label="来源">
            <Input v-model="formData.source" placeholder="请输入来源" />
          </FormItem>
        </Form>
      </Modal>

      <!-- 导入对话框 -->
      <Modal
        v-model="importModalVisible"
        title="从项目平台导入"
        width="1200"
        footer-hide
      >
        <div style="margin-bottom: 15px;">
          <Button type="primary" @click="fetchImportData" :loading="importLoading">获取数据</Button>
          <span style="margin-left: 10px; color: var(--subtext2);">
            共 {{ importData.length }} 条数据，可导入 {{ importData.filter(item => item.importStatus === 'new').length }} 条
          </span>
        </div>
        <div style="max-height: 600px; overflow-y: auto;">
          <BaseTable
            :columns="importColumns"
            :data="importData"
            :loading="importLoading"
            border
            selectable="multiple"
            :selection="selectedImportItems"
            @event="handleImportTableEvent"
          />
        </div>
        <div style="margin-top: 15px; text-align: right;">
          <Button @click="importModalVisible = false" style="margin-right: 10px;">关闭</Button>
          <Button type="primary" @click="handleImportSubmit" :loading="importSubmitting" :disabled="selectedImportItems.length === 0">
            导入选中项 ({{ selectedImportItems.length }})
          </Button>
        </div>
      </Modal>
    </div>
</template>

<script>
import App from '@/components/app/app';
import AppBar from '@/components/app/bar';
import AppClient from '@/components/app/client';
import AppTitle from '@/components/app/title';
import UTIL from '@/utils';
import moment from 'moment';

export default {
  components: {
    App,
    AppBar,
    AppClient,
    AppTitle,
  },
  data() {
    return {
      loading: false,
      items: [],
      searchText: '',
      filterType: '',
      filterSyncState: null,
      modalVisible: false,
      modalTitle: '新增知识条目',
      confirmLoading: false,
      currentId: null,
      formData: {
        name: '',
        type: '',
        content: '',
        files: '',
        author: '',
        source: '',
        knowledge_collection_id: '7581773912599822370',
      },
      rules: {
        name: [{ required: true, message: '请输入知识条目总结', trigger: 'blur' }],
        type: [{ required: true, message: '请输入条目类型', trigger: 'blur' }],
        content: [{ required: true, message: '请输入知识库内容', trigger: 'blur' }],
      },
      importModalVisible: false,
      importLoading: false,
      importSubmitting: false,
      importData: [],
      selectedImportItems: [],
      localMd5Map: {}, // 本地数据的MD5映射
      columns: [
        { type: 'index', width: 80, title: '序号' },
        {
          title: '知识条目总结',
          key: 'name',
          minWidth: 200,
          align: 'left',
        },
        {
          title: '条目类型',
          key: 'type',
          width: 150,
        },
        {
          title: '作者',
          key: 'author',
          width: 120,
          render: (h, params) => {
            return h('span', params.row.author || '-');
          },
        },
        {
          title: '来源',
          key: 'source',
          width: 150,
          render: (h, params) => {
            return h('span', params.row.source || '-');
          },
        },
        {
          title: '同步状态',
          key: 'sync_state',
          width: 120,
          align: 'center',
          render: (h, params) => {
            const stateMap = {
              0: { text: '未同步', color: 'default' },
              1: { text: '已更新', color: 'warning' },
              2: { text: '切片中', color: 'processing' },
              3: { text: '已同步', color: 'success' },
            };
            const state = stateMap[params.row.sync_state] || stateMap[0];
            return h('Tag', {
              props: {
                color: state.color,
              },
            }, state.text);
          },
        },
        {
          title: '最后同步时间',
          key: 'sync_at',
          width: 180,
          render: (h, params) => {
            if (!params.row.sync_at) return h('span', '-');
            return h('span', moment(params.row.sync_at).format('YYYY-MM-DD HH:mm:ss'));
          },
        },
        {
          title: '创建时间',
          key: 'created_at',
          width: 180,
          render: (h, params) => {
            if (!params.row.created_at) return h('span', '-');
            return h('span', moment(params.row.created_at).format('YYYY-MM-DD HH:mm:ss'));
          },
        },
        {
          title: '操作',
          key: 'action',
          fixed: 'right',
          width: 150,
          type: 'tool',
          buttons: [
            {
              event: 'edit',
              label: '编辑',
            },
            {
              event: 'delete',
              label: '删除',
            },
          ],
        },
      ],
      importColumns: [
        { type: 'index', width: 60, title: '序号' },
        {
          title: '名称',
          key: 'name',
          minWidth: 200,
        },
        {
          title: '类型',
          key: 'type',
          width: 150,
        },
        {
          title: '作者',
          key: 'author',
          width: 120,
          render: (h, params) => {
            return h('span', params.row.author || '-');
          },
        },
        {
          title: '来源',
          key: 'source',
          width: 150,
          render: (h, params) => {
            return h('span', params.row.source || '-');
          },
        },
        {
          title: '备注',
          key: 'note',
          width: 150,
          render: (h, params) => {
            return h('span', params.row.note || '-');
          },
        },
        {
          title: '导入状态',
          key: 'importStatus',
          width: 120,
          align: 'center',
          render: (h, params) => {
            const status = params.row.importStatus;
            if (status === 'exists') {
              return h('Tag', {
                props: {
                  color: 'warning',
                },
              }, '已存在');
            } else if (status === 'new') {
              return h('Tag', {
                props: {
                  color: 'success',
                },
              }, '可导入');
            } else {
              return h('Tag', {
                props: {
                  color: 'default',
                },
              }, '待检查');
            }
          },
        },
        {
          title: '内容预览',
          key: 'content',
          minWidth: 200,
          render: (h, params) => {
            const content = params.row.content || '';
            const preview = content.length > 100 ? content.substring(0, 100) + '...' : content;
            return h('span', {
              attrs: {
                title: content,
              },
              style: {
                color: 'var(--text2)',
              },
            }, preview || '-');
          },
        },
      ],
    };
  },
  computed: {
    typeList() {
      const types = new Set();
      this.items.forEach(item => {
        if (item.type) {
          types.add(item.type);
        }
      });
      return Array.from(types);
    },
    filteredData() {
      let result = [...this.items];
      
      if (this.searchText) {
        const query = this.searchText.toLowerCase();
        result = result.filter(item => 
          (item.name && item.name.toLowerCase().includes(query)) ||
          (item.content && item.content.toLowerCase().includes(query))
        );
      }
      
      if (this.filterType) {
        result = result.filter(item => item.type === this.filterType);
      }
      
      if (this.filterSyncState !== null && this.filterSyncState !== '') {
        result = result.filter(item => item.sync_state === this.filterSyncState);
      }
      
      return result;
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.fetchData();
    });
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await this.$api.get('/ai/knowledges');
        this.items = response.data.data || [];
        // 构建本地MD5映射
        this.localMd5Map = {};
        this.items.forEach(item => {
          if (item.md5) {
            this.localMd5Map[item.md5] = item;
          }
        });
      } catch (error) {
        this.$Message.error('获取知识库列表失败');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      // 搜索逻辑在computed中处理
    },
    handleAdd() {
      this.modalTitle = '新增知识条目';
      this.currentId = null;
      this.formData = {
        name: '',
        type: '',
        content: '',
        files: '',
        author: '',
        source: '',
        knowledge_collection_id: '7581773912599822370',
      };
      this.modalVisible = true;
    },
    handleTableEvent(event, row) {
      if (event === 'edit') {
        this.handleEdit(row);
      } else if (event === 'delete') {
        this.handleDelete(row);
      }
    },
    async handleEdit(record) {
      this.modalTitle = '编辑知识条目';
      this.currentId = record.id;
      try {
        const response = await this.$api.get(`/ai/knowledges/${record.id}`);
        this.formData = { ...response.data.data };
        if (this.formData.files && Array.isArray(this.formData.files)) {
          this.formData.files = this.formData.files.join('\n');
        } else if (typeof this.formData.files === 'string') {
          // 已经是字符串，保持不变
        } else {
          this.formData.files = '';
        }
        this.modalVisible = true;
      } catch (error) {
        this.$Message.error('获取知识条目详情失败');
        console.error(error);
      }
    },
    async handleDelete(record) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除知识条目"${record.name}"吗？`,
        onOk: async () => {
          try {
            await this.$api.delete(`/ai/knowledges/${record.id}`);
            this.$Message.success('删除成功');
            this.fetchData();
          } catch (error) {
            this.$Message.error('删除失败');
            console.error(error);
          }
        },
      });
    },
    async handleSubmit() {
      try {
        await this.$refs.formRef.validate();
        this.confirmLoading = true;
        
        const submitData = { ...this.formData };
        
        // 处理files字段
        if (submitData.files) {
          const filesArray = submitData.files.split('\n').filter(f => f.trim());
          submitData.files = filesArray.length > 0 ? filesArray : null;
        } else {
          submitData.files = null;
        }
        
        // 计算MD5
        if (submitData.content) {
          submitData.md5 = UTIL.MD5(submitData.content);
        }
        
        if (this.currentId) {
          // 更新
          await this.$api.patch(`/ai/knowledges/${this.currentId}`, submitData);
          this.$Message.success('更新成功');
        } else {
          // 新增
          await this.$api.post('/ai/knowledges', submitData);
          this.$Message.success('添加成功');
        }
        
        this.modalVisible = false;
        this.fetchData();
      } catch (error) {
        if (error.message) {
          this.$Message.error(error.message);
        }
        console.error(error);
      } finally {
        this.confirmLoading = false;
      }
    },
    handleCancel() {
      this.modalVisible = false;
      this.formData = {
        name: '',
        type: '',
        content: '',
        files: '',
        author: '',
        source: '',
        knowledge_collection_id: '7581773912599822370',
      };
    },
    handleImport() {
      this.importModalVisible = true;
      this.importData = [];
      this.selectedImportItems = [];
    },
    async fetchImportData() {
      this.importLoading = true;
      try {
        // 先获取本地数据以构建MD5映射
        if (Object.keys(this.localMd5Map).length === 0) {
          const localResponse = await this.$api.get('/ai/knowledges');
          this.items = localResponse.data.data || [];
          this.localMd5Map = {};
          this.items.forEach(item => {
            if (item.md5) {
              this.localMd5Map[item.md5] = item;
            }
          });
        }
        
        // 获取项目平台数据
        const response = await this.$api.get('/logined_zzl/zzl_table_data?table_fID=69391c12e3b7c');
        const remoteData = response.data.data || [];
        
        // 处理数据，计算MD5并比对
        this.importData = remoteData.map((item, index) => {
          const content = item.content || '';
          const md5 = UTIL.MD5(content);
          const exists = this.localMd5Map[md5] !== undefined;
          
          return {
            ...item,
            id: item.id || `import_${index}_${md5}`, // 确保有唯一id
            md5,
            importStatus: exists ? 'exists' : 'new',
            localItem: exists ? this.localMd5Map[md5] : null,
          };
        });
        
        // 按类型分组显示
        const typeGroups = {};
        this.importData.forEach(item => {
          if (!typeGroups[item.type]) {
            typeGroups[item.type] = [];
          }
          typeGroups[item.type].push(item);
        });
        
        this.$Message.success(`获取到 ${this.importData.length} 条数据`);
      } catch (error) {
        this.$Message.error('获取项目平台数据失败');
        console.error(error);
      } finally {
        this.importLoading = false;
      }
    },
    handleImportTableEvent(event) {
      if (event.type === 'select') {
        // 获取选中的完整数据对象
        if (Array.isArray(event.data)) {
          // 多选模式，event.data是选中项的id数组
          this.selectedImportItems = this.importData.filter(item => 
            event.data.includes(item.id)
          );
        } else if (event.data) {
          // 单选模式
          this.selectedImportItems = [this.importData.find(item => item.id === event.data)].filter(Boolean);
        } else {
          this.selectedImportItems = [];
        }
      }
    },
    async handleImportSubmit() {
      // 只导入选中的且状态为new的项
      const toImport = this.selectedImportItems.filter(item => item.importStatus === 'new');
      
      if (toImport.length === 0) {
        this.$Message.warning('请选择要导入的数据（只能导入标记为"可导入"的数据）');
        return;
      }
      
      this.$Modal.confirm({
        title: '确认导入',
        content: `确定要导入 ${toImport.length} 条新数据吗？`,
        onOk: async () => {
          this.importSubmitting = true;
          try {
            const importPromises = toImport.map(item => {
              const submitData = {
                name: item.name || '',
                type: item.type || '',
                content: item.content || '',
                author: item.author || '',
                source: item.source || '',
                knowledge_collection_id: '7581773912599822370',
                md5: item.md5,
                sync_state: 0,
              };
              
              return this.$api.post('/ai/knowledges', submitData);
            });
            
            await Promise.all(importPromises);
            this.$Message.success(`成功导入 ${toImport.length} 条数据`);
            this.importModalVisible = false;
            this.selectedImportItems = [];
            this.fetchData();
          } catch (error) {
            this.$Message.error('导入失败');
            console.error(error);
          } finally {
            this.importSubmitting = false;
          }
        },
      });
    },
  },
};
</script>

<style lang="less" scoped>
.search-bar {
  background: var(--bg3);
}
</style>

